import { useMemo, useState, type ReactNode } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  Button,
  FormControlLabel,
  Link as MuiLink,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Inventory2Outlined as PackagesIcon,
  TimelineOutlined as ActivityIcon,
  RocketLaunchOutlined as RolloutsIcon,
  ErrorOutline as FailureIcon,
  WarningAmberOutlined as ProblemDeviceIcon,
  ManageSearchOutlined as ExplorerIcon,
  InfoOutlined as InfoIcon,
  Close as ClearIcon,
  HourglassBottomOutlined as StuckIcon,
} from '@mui/icons-material';
import {
  useSoftwareUpdateOverview,
  useOverviewEventsTimeseries,
  useOverviewEvents,
  useOverviewStuckInstalls,
} from '@/hooks/use-software-update-overview';
import type {
  OverviewEvent,
  OverviewPackage,
  OverviewProblemDevice,
  OverviewRollout,
  OverviewRolloutStats,
  OverviewStuckInstall,
  OverviewTotals,
} from '@/lib/software-update-overview.service';
import type { RolloutType } from '@/lib/software-update-rollout.service';
import type { PackageType } from '@/lib/software-update-package.service';
import {
  EventsTimeseriesChart,
  ROLLOUT_EVENT_TYPES,
} from '@/components/software-update/EventsTimeseriesChart';
import {
  CardSectionHeader,
  SECTION_ICON_COLOR,
} from '@/components/software-update/overview-card';
import { HeaderActions } from '@/components/software-update/header-actions-slot';
import {
  Dash,
  EventChip,
  EventFromVersionCell,
  EventToVersionCell,
  EventTimeCell,
  EVENT_ROW_SX,
  ReasonChip,
} from '@/components/software-update/event-cells';
import { RolloutTargetInline } from '@/components/software-update/rollout-target-inline';
import { TableSkeleton } from '@/components/ui/table-skeleton';
import { TextLink } from '@/components/ui/text-link';
import { CopyDeviceId } from '@/components/ui/copy-button';
import { InternalDeviceIcon } from '@/components/ui/internal-device-icon';
import { FormattedTime } from '@/components/ui/formatted-time';
import { getDeviceUpdateEventVisual } from '@/lib/software-update-config';
import { useTimezone } from '@/hooks/use-timezone';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { colors, typography as t, radius } from '@/theme/tokens';

// ─── Time window ─────────────────────────────────────────────────────────────
// One shared window drives every section. `bucket_seconds` is derived per window
// (rollout-detail lets the server pick the bucket; the overview timeseries
// endpoint requires the bucket size as a param, so it is computed here to keep
// ~60–168 buckets across the range).

const MIN = 60 * 1000;
const HOUR = 60 * MIN;
const DAY = 24 * HOUR;

const WINDOW_PRESETS = [
  { key: '1h', label: '1h', ms: HOUR, bucketSeconds: MIN / 1000 },
  { key: '6h', label: '6h', ms: 6 * HOUR, bucketSeconds: 5 * MIN / 1000 },
  { key: '24h', label: '24h', ms: DAY, bucketSeconds: 15 * MIN / 1000 },
  { key: '7d', label: '7d', ms: 7 * DAY, bucketSeconds: HOUR / 1000 },
  { key: '30d', label: '30d', ms: 30 * DAY, bucketSeconds: 6 * HOUR / 1000 },
  { key: '90d', label: '90d', ms: 90 * DAY, bucketSeconds: DAY / 1000 },
] as const;
type WindowKey = (typeof WINDOW_PRESETS)[number]['key'];
const DEFAULT_WINDOW: WindowKey = '24h';

interface TimeWindow {
  since: string;
  until: string;
}

function snapshotWindow(key: WindowKey): TimeWindow {
  const until = new Date();
  const preset =
    WINDOW_PRESETS.find((w) => w.key === key) ??
    WINDOW_PRESETS.find((w) => w.key === DEFAULT_WINDOW)!;
  return { since: new Date(until.getTime() - preset.ms).toISOString(), until: until.toISOString() };
}

// "Sep 2, 09:01 AM – Sep 3, 09:01 AM": the concrete range behind the preset, so
// the caption answers "as of when?" without a second control.
function formatWindowRange(range: TimeWindow): string {
  const opts: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  };
  const since = new Date(range.since);
  const until = new Date(range.until);
  if (Number.isNaN(since.getTime()) || Number.isNaN(until.getTime())) return '';
  return `${since.toLocaleString([], opts)} – ${until.toLocaleString([], opts)}`;
}

function bucketSecondsFor(key: WindowKey): number {
  return (
    WINDOW_PRESETS.find((w) => w.key === key) ??
    WINDOW_PRESETS.find((w) => w.key === DEFAULT_WINDOW)!
  ).bucketSeconds;
}

const ALL = '__all__';
const EVENT_PAGE_LIMIT = 15;
const PROBLEM_DEVICE_PAGE_LIMIT = 10;
const STUCK_INSTALL_PAGE_LIMIT = 10;
// An OS item counts as stuck once this long has passed since its download
// success was recorded without an install result.
const STUCK_AFTER_SECONDS = 24 * 60 * 60;
const STUCK_INSTALL_HELP =
  'OS downloads that succeeded 24h+ ago (server time) and are still waiting for an install success or failure for that exact attempt. Shows everything stuck right now, regardless of the selected time window.';

function overviewPackageLabel(pkg: OverviewPackage): string {
  return pkg.display_name || pkg.package_name || pkg.type.toUpperCase();
}

function multiSelectValues(value: string | string[]): string[] {
  const values = typeof value === 'string' ? value.split(',') : value;
  return values.includes(ALL) ? [] : values;
}

function multiSelectSummary(
  selected: readonly string[],
  emptyLabel: string,
  pluralLabel: string,
  labels?: ReadonlyMap<string, string>,
): string {
  if (selected.length === 0) return emptyLabel;
  if (selected.length === 1) return labels?.get(selected[0]) ?? selected[0];
  return `${selected.length} ${pluralLabel}`;
}

// ─── Health (derived client-side, never stored) ──────────────────────────────

type Health = 'failing' | 'degraded' | 'healthy' | 'no_activity';

const HEALTH_ORDER: Record<Health, number> = {
  failing: 0,
  degraded: 1,
  healthy: 2,
  no_activity: 3,
};

const HEALTH_CONFIG: Record<Health, { label: string; color: string; bg: string }> = {
  failing: { label: 'Failing', color: colors.error.dark, bg: colors.error.light },
  degraded: { label: 'Degraded', color: colors.warning.dark, bg: colors.warning.light },
  healthy: { label: 'Healthy', color: colors.success.dark, bg: colors.success.light },
  no_activity: { label: 'No activity', color: colors.text.secondary, bg: colors.grey[50] },
};

const HEALTH_THRESHOLD_HELP = 'Healthy: <0.5% errors · Degraded: 0.5–<5% · Failing: ≥5%';

function terminalTotal(s: OverviewRolloutStats): number {
  return (
    s.download_success +
    s.download_failed +
    s.download_rejected +
    s.install_success +
    s.install_failed
  );
}

function eventTotal(s: OverviewRolloutStats): number {
  return terminalTotal(s);
}

function computeHealth(s: OverviewRolloutStats): Health {
  if (s.error_rate >= 0.05) return 'failing';
  if (s.error_rate >= 0.005) return 'degraded';
  if (terminalTotal(s) === 0) return 'no_activity';
  return 'healthy';
}

function HelpTip({ title }: { title: string }) {
  return (
    <Tooltip title={title} arrow>
      <Box
        component="span"
        tabIndex={0}
        aria-label={title}
        sx={{ display: 'inline-flex', cursor: 'help' }}
      >
        <InfoIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
      </Box>
    </Tooltip>
  );
}

function HealthThresholdHelp() {
  return <HelpTip title={HEALTH_THRESHOLD_HELP} />;
}

const ROLLOUT_TYPE_CHIP: Record<RolloutType, { bg: string; color: string; label: string }> = {
  app: { ...colors.packageType.apk, label: 'APP' },
  os: { ...colors.packageType.os, label: 'OS' },
  fw: { ...colors.packageType.fw, label: 'FW' },
};

// "Now rolling out" chip order: firmware, then OS, then apps.
const PACKAGE_TYPE_SORT: Record<PackageType, number> = { fw: 0, os: 1, apk: 2 };

const TABLE_SX = {
  '& thead th': {
    bgcolor: 'grey.50',
    borderBottom: '2px solid',
    borderColor: 'divider',
  },
} as const;

const EXPLORER_LINK_SX = {
  fontSize: 13,
  color: 'text.primary',
  '&:hover, &:focus-visible': {
    color: colors.primary.main,
    textDecoration: 'underline',
  },
} as const;

// ─── Page ─────────────────────────────────────────────────────────────────────

export function SoftwareUpdateOverviewPage() {
  const navigate = useNavigate();

  const [windowKey, setWindowKey] = useState<WindowKey>(DEFAULT_WINDOW);
  const [range, setRange] = useState<TimeWindow>(() => snapshotWindow(DEFAULT_WINDOW));

  // Cross-rollout explorer filters.
  const [expEventTypes, setExpEventTypes] = useState<string[]>([]);
  const [expRolloutIds, setExpRolloutIds] = useState<string[]>([]);
  const [expReasons, setExpReasons] = useState<string[]>([]);
  const [expMachineId, setExpMachineId] = useState<string>('');
  const [expOffset, setExpOffset] = useState(0);
  const [problemDeviceOffset, setProblemDeviceOffset] = useState(0);
  const [stuckOffset, setStuckOffset] = useState(0);
  // Internal devices are excluded by default so the overview reflects the
  // customer fleet; the toggle folds them back into every aggregate.
  const [includeInternal, setIncludeInternal] = useState(false);

  const bucketSeconds = bucketSecondsFor(windowKey);

  const overview = useSoftwareUpdateOverview({
    since: range.since,
    until: range.until,
    problem_device_offset: problemDeviceOffset,
    include_internal: includeInternal,
  });
  const timeseries = useOverviewEventsTimeseries({
    since: range.since,
    until: range.until,
    bucket_seconds: bucketSeconds,
    include_internal: includeInternal,
  });
  const events = useOverviewEvents({
    since: range.since,
    until: range.until,
    limit: EVENT_PAGE_LIMIT,
    offset: expOffset,
    event_type: expEventTypes,
    rollout_id: expRolloutIds,
    reason: expReasons,
    machine_id: expMachineId.trim() || undefined,
    include_internal: includeInternal,
  });
  // Snapshot of current stuck OS installs; independent of the time window.
  const stuck = useOverviewStuckInstalls({
    stuck_after_seconds: STUCK_AFTER_SECONDS,
    offset: stuckOffset,
    include_internal: includeInternal,
  });

  const overviewData = overview.data;
  const totals = overviewData?.totals;
  // Memoized so the useMemo hooks below don't see a fresh `?? []` array each
  // render (react-query returns a stable object reference while data is unchanged).
  const rollouts = useMemo(() => overviewData?.rollouts ?? [], [overviewData]);
  const packages = useMemo(() => overviewData?.packages ?? [], [overviewData]);
  const failureReasons = useMemo(() => overviewData?.failure_reasons ?? [], [overviewData]);
  const problemDevices = overviewData?.problem_devices ?? [];
  const problemDevicesTotal = overviewData?.problem_devices_total ?? 0;
  const overviewUnavailable = overview.isError && !overviewData;

  const isFetching =
    overview.isFetching || timeseries.isFetching || events.isFetching || stuck.isFetching;

  const handleWindowChange = (next: WindowKey) => {
    setWindowKey(next);
    setRange(snapshotWindow(next));
    setExpOffset(0);
    setProblemDeviceOffset(0);
  };

  // Changing the device scope re-aggregates every section, so paginated views
  // return to their first page while filters and the window are preserved.
  const handleIncludeInternalChange = (next: boolean) => {
    setIncludeInternal(next);
    setExpOffset(0);
    setProblemDeviceOffset(0);
    setStuckOffset(0);
  };

  const handleRefresh = () => {
    // Re-snapshot so `until` slides to now; the param change refetches every read.
    setRange(snapshotWindow(windowKey));
    setExpOffset(0);
    setProblemDeviceOffset(0);
    // The stuck snapshot has no window params to slide, so refetch it directly;
    // returning to page 1 refetches through the key change instead.
    if (stuckOffset === 0) void stuck.refetch();
    else setStuckOffset(0);
  };

  const explorerFiltered =
    expEventTypes.length > 0 ||
    expRolloutIds.length > 0 ||
    expReasons.length > 0 ||
    expMachineId.trim() !== '';

  const clearExplorerFilters = () => {
    setExpEventTypes([]);
    setExpRolloutIds([]);
    setExpReasons([]);
    setExpMachineId('');
    setExpOffset(0);
  };

  // Sort: worst-first by health, then error_rate desc within a band.
  const sortedRollouts = useMemo(() => {
    return [...rollouts].sort((a, b) => {
      const ha = HEALTH_ORDER[computeHealth(a.stats)];
      const hb = HEALTH_ORDER[computeHealth(b.stats)];
      if (ha !== hb) return ha - hb;
      return b.stats.error_rate - a.stats.error_rate;
    });
  }, [rollouts]);

  // Most widely used packages first. Type, event volume, and name provide
  // deterministic tie-breakers.
  const sortedPackages = useMemo(
    () =>
      [...packages].sort(
        (a, b) =>
          b.rollout_count - a.rollout_count ||
          PACKAGE_TYPE_SORT[a.type] - PACKAGE_TYPE_SORT[b.type] ||
          b.event_count - a.event_count ||
          overviewPackageLabel(a).localeCompare(overviewPackageLabel(b)),
      ),
    [packages],
  );

  const healthCounts = useMemo(() => {
    const counts: Record<Health, number> = { failing: 0, degraded: 0, healthy: 0, no_activity: 0 };
    for (const r of rollouts) counts[computeHealth(r.stats)] += 1;
    return counts;
  }, [rollouts]);

  const rolloutOptions = useMemo(
    () =>
      [...rollouts]
        .map((r) => r.rollout)
        .sort((a, b) => (a.name || '').localeCompare(b.name || '')),
    [rollouts],
  );

  // Reason options for the explorer: the window's top reasons, plus any reason on
  // the currently loaded event rows, plus the active selection (so a narrowed set
  // can't strand the user on a value they can't switch away from).
  const reasonOptions = useMemo(() => {
    const set = new Set<string>();
    for (const r of failureReasons) set.add(r.reason);
    for (const ev of events.data?.events ?? []) if (ev.reason) set.add(ev.reason);
    for (const reason of expReasons) set.add(reason);
    return Array.from(set).sort();
  }, [failureReasons, events.data?.events, expReasons]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <HeaderActions>
        <Button
          variant="outlined"
          size="small"
          startIcon={isFetching ? <CircularProgress size={16} /> : <RefreshIcon sx={{ fontSize: 16 }} />}
          onClick={handleRefresh}
          disabled={isFetching}
          sx={{
            borderColor: 'divider',
            color: 'text.secondary',
            borderRadius: `${radius.md}px`,
            textTransform: 'none',
          }}
        >
          Refresh
        </Button>
      </HeaderActions>

      {/* 1 — Window toolbar. A bare row, not a card: one control and its caption
          don't need a container of their own. */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', px: 0.5 }}>
        <Typography variant="caption" color="text.secondary">
          Window
        </Typography>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={windowKey}
          onChange={(_, v: WindowKey | null) => v && handleWindowChange(v)}
          aria-label="Time window"
          sx={{
            bgcolor: 'background.paper',
            '& .MuiToggleButton-root': {
              fontSize: 11.5,
              px: 1.5,
              py: 0.25,
              textTransform: 'none',
              borderColor: 'divider',
              color: 'text.secondary',
              '&.Mui-selected': {
                color: 'text.primary',
                fontWeight: t.fontWeight.semibold,
              },
            },
          }}
        >
          {WINDOW_PRESETS.map((w) => (
            <ToggleButton key={w.key} value={w.key}>
              {w.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={includeInternal}
              onChange={(e) => handleIncludeInternalChange(e.target.checked)}
              // Strip the default hit-area padding so the box sits flush with the
              // toolbar baseline; the label margin below provides the gap.
              disableRipple
              sx={{ p: 0, '& svg': { fontSize: 18 } }}
            />
          }
          label="Include internal devices"
          sx={{
            m: 0,
            gap: 0.75,
            '& .MuiFormControlLabel-label': {
              fontSize: 11.5,
              lineHeight: 1,
              color: 'text.secondary',
            },
          }}
        />
        <Typography
          variant="caption"
          color="text.disabled"
          sx={{
            ml: { xs: 0, sm: 'auto' },
            flexBasis: { xs: '100%', sm: 'auto' },
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          Active rollouts · {formatWindowRange(range)}
        </Typography>
      </Box>

      {overview.isError && (
        <QueryError
          message={overviewData ? 'Failed to refresh overview data. Showing the previous result.' : 'Failed to load overview data.'}
          retrying={overview.isFetching}
          onRetry={() => void overview.refetch()}
        />
      )}

      {/* 2 — KPI strip */}
      {!overviewUnavailable && (
        <KpiStrip
          totals={totals}
          loading={overview.isLoading}
          healthCounts={healthCounts}
        />
      )}

      {/* 3 — Now rolling out */}
      {!overviewUnavailable && <Card>
        <CardSectionHeader
          title="Now rolling out"
          icon={<PackagesIcon sx={{ color: SECTION_ICON_COLOR, fontSize: 20 }} />}
        />
        <CardContent sx={{ pt: 1.5 }}>
          {overview.isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={20} />
            </Box>
          ) : sortedPackages.length === 0 ? (
            <EmptyRow>No packages attached to active rollouts.</EmptyRow>
          ) : (
            <PackageRolloutInspector packages={sortedPackages} rollouts={sortedRollouts} />
          )}
        </CardContent>
      </Card>}

      {/* 4 — Fleet activity */}
      <Card sx={{ overflow: 'hidden' }}>
        <CardSectionHeader
          title="Update activity"
          icon={<ActivityIcon sx={{ color: SECTION_ICON_COLOR, fontSize: 20 }} />}
        />
        <Box sx={{ pt: 1 }}>
          {timeseries.isError ? (
            <QueryError
              message="Failed to load update activity."
              retrying={timeseries.isFetching}
              onRetry={() => void timeseries.refetch()}
              inset
            />
          ) : (
            <EventsTimeseriesChart
              buckets={timeseries.data?.buckets ?? []}
              bucketSize={timeseries.data?.bucket_size ?? ''}
              loading={timeseries.isLoading}
              emptyText="No events across active rollouts in this window."
            />
          )}
        </Box>
      </Card>

      {/* 5 — Active rollouts */}
      {!overviewUnavailable && <Card sx={{ overflow: 'hidden' }}>
        <CardSectionHeader
          title="Active rollouts"
          icon={<RolloutsIcon sx={{ color: SECTION_ICON_COLOR, fontSize: 20 }} />}
        />
        <TableContainer>
          <Table size="small" sx={TABLE_SX}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 120 }}>
                  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                    Health
                    <HealthThresholdHelp />
                  </Box>
                </TableCell>
                <TableCell>Rollout</TableCell>
                <TableCell sx={{ width: 70 }}>Type</TableCell>
                <TableCell>Target</TableCell>
                <TableCell align="right" sx={{ width: 84 }}>Download success</TableCell>
                <TableCell align="right" sx={{ width: 84 }}>Install success</TableCell>
                <TableCell align="right" sx={{ width: 70 }}>Fail</TableCell>
                <TableCell sx={{ width: 150 }}>Error rate</TableCell>
                <TableCell sx={{ width: 104 }}>Activity</TableCell>
                <TableCell align="right" sx={{ width: 96 }}>Last event</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {overview.isLoading ? (
                <TableSkeleton rows={6} columns={10} />
              ) : sortedRollouts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} sx={{ py: 6, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      No active rollouts.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                sortedRollouts.map((r) => (
                  <RolloutRow key={r.rollout.id} entry={r} />
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>}

      {/* 6 — Stuck OS installs: a current snapshot, independent of the window */}
      <StuckInstallsCard
        items={stuck.data?.items ?? []}
        total={stuck.data?.total ?? 0}
        asOf={stuck.data?.as_of ?? ''}
        loading={stuck.isLoading}
        fetching={stuck.isFetching}
        error={stuck.isError}
        hasData={!!stuck.data}
        offset={stuckOffset}
        onRetry={() => void stuck.refetch()}
        onPrev={() => setStuckOffset((offset) => Math.max(0, offset - STUCK_INSTALL_PAGE_LIMIT))}
        onNext={() => setStuckOffset((offset) => offset + STUCK_INSTALL_PAGE_LIMIT)}
      />

      {/* 7 — Failure reasons + problem devices */}
      {!overviewUnavailable && <Box
        sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns: { xs: '1fr', md: '1fr 2fr' },
        }}
      >
        <Card sx={{ overflow: 'hidden' }}>
          <CardSectionHeader
            title="Top failure reasons"
            icon={<FailureIcon sx={{ color: SECTION_ICON_COLOR, fontSize: 20 }} />}
          />
          <CardContent sx={{ pt: 1.5 }}>
            {overview.isLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress size={20} />
              </Box>
            ) : failureReasons.length === 0 ? (
              <EmptyRow>No failures in this window.</EmptyRow>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                {failureReasons.map((r) => (
                  <ReasonBar
                    key={r.reason}
                    reason={r.reason}
                    count={r.count}
                    max={failureReasons[0].count || 1}
                  />
                ))}
              </Box>
            )}
          </CardContent>
        </Card>

        <Card sx={{ overflow: 'hidden' }}>
          <CardSectionHeader
            title="Problem devices"
            icon={<ProblemDeviceIcon sx={{ color: SECTION_ICON_COLOR, fontSize: 20 }} />}
          />
          <TableContainer>
            <Table size="small" sx={{ ...TABLE_SX, tableLayout: 'fixed' }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: '25%' }}>Device</TableCell>
                  <TableCell align="center" sx={{ width: '25%' }}>Fails</TableCell>
                  <TableCell sx={{ width: '25%' }}>Last reason</TableCell>
                  <TableCell align="right" sx={{ width: '25%' }}>Last</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {overview.isLoading ? (
                  <TableSkeleton rows={5} columns={4} />
                ) : problemDevices.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} sx={{ py: 5, textAlign: 'center' }}>
                      <Typography variant="body2" color="text.secondary">
                        No devices with failures in this window.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  problemDevices.map((d) => (
                    <ProblemDeviceRow key={d.device_id} device={d} />
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <ExplorerPager
            offset={problemDeviceOffset}
            limit={PROBLEM_DEVICE_PAGE_LIMIT}
            total={problemDevicesTotal}
            loading={overview.isFetching}
            onPrev={() =>
              setProblemDeviceOffset((offset) => Math.max(0, offset - PROBLEM_DEVICE_PAGE_LIMIT))
            }
            onNext={() => setProblemDeviceOffset((offset) => offset + PROBLEM_DEVICE_PAGE_LIMIT)}
          />
        </Card>
      </Box>}

      {/* 8 — Event explorer */}
      <Card sx={{ overflow: 'hidden' }}>
        <CardSectionHeader
          title="Event explorer"
          icon={<ExplorerIcon sx={{ color: SECTION_ICON_COLOR, fontSize: 20 }} />}
        />
        <Box sx={{ px: 2, pt: 1.5, pb: 1.5 }}>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
              Filter:
            </Typography>
            <Select
              multiple
              displayEmpty
              size="small"
              value={expEventTypes}
              onChange={(e) => {
                setExpEventTypes(multiSelectValues(e.target.value));
                setExpOffset(0);
              }}
              renderValue={(selected) => multiSelectSummary(selected, 'All events', 'events')}
              sx={{ minWidth: 170, fontSize: 12 }}
            >
              <MenuItem value={ALL}>
                <Checkbox size="small" checked={expEventTypes.length === 0} />
                All events
              </MenuItem>
              {ROLLOUT_EVENT_TYPES.map((et) => (
                <MenuItem key={et} value={et}>
                  <Checkbox size="small" checked={expEventTypes.includes(et)} />
                  {et}
                </MenuItem>
              ))}
            </Select>
            <Select
              multiple
              displayEmpty
              size="small"
              value={expRolloutIds}
              onChange={(e) => {
                setExpRolloutIds(multiSelectValues(e.target.value));
                setExpOffset(0);
              }}
              renderValue={(selected) =>
                multiSelectSummary(
                  selected,
                  'All active rollouts',
                  'rollouts',
                  new Map(rolloutOptions.map((r) => [r.id, r.name || '(unnamed)'])),
                )
              }
              sx={{ minWidth: 200, fontSize: 12 }}
            >
              <MenuItem value={ALL}>
                <Checkbox size="small" checked={expRolloutIds.length === 0} />
                All active rollouts
              </MenuItem>
              {rolloutOptions.map((r) => (
                <MenuItem key={r.id} value={r.id}>
                  <Checkbox size="small" checked={expRolloutIds.includes(r.id)} />
                  {r.name || '(unnamed)'}
                </MenuItem>
              ))}
            </Select>
            <Select
              multiple
              displayEmpty
              size="small"
              value={expReasons}
              onChange={(e) => {
                setExpReasons(multiSelectValues(e.target.value));
                setExpOffset(0);
              }}
              renderValue={(selected) => multiSelectSummary(selected, 'All reasons', 'reasons')}
              sx={{ minWidth: 200, fontSize: 12 }}
            >
              <MenuItem value={ALL}>
                <Checkbox size="small" checked={expReasons.length === 0} />
                All reasons
              </MenuItem>
              {reasonOptions.map((r) => (
                <MenuItem key={r} value={r}>
                  <Checkbox size="small" checked={expReasons.includes(r)} />
                  {r}
                </MenuItem>
              ))}
            </Select>
            <TextField
              size="small"
              placeholder="Machine ID"
              value={expMachineId}
              onChange={(e) => {
                setExpMachineId(e.target.value);
                setExpOffset(0);
              }}
              sx={{ minWidth: 220, '& .MuiInputBase-input': { fontSize: 12 } }}
            />
            {explorerFiltered && (
              <Button
                size="small"
                variant="text"
                startIcon={<ClearIcon sx={{ fontSize: 14 }} />}
                onClick={clearExplorerFilters}
                sx={{ color: 'text.secondary', textTransform: 'none', fontSize: 12, py: 0.5 }}
              >
                Clear
              </Button>
            )}
          </Box>
        </Box>

        <TableContainer>
          <Table size="small" sx={TABLE_SX}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 176 }}>Server time</TableCell>
                <TableCell>Device</TableCell>
                <TableCell>Rollout</TableCell>
                <TableCell>Package</TableCell>
                <TableCell sx={{ width: 140 }}>From version</TableCell>
                <TableCell sx={{ width: 140 }}>To version</TableCell>
                <TableCell sx={{ width: 160 }}>Event</TableCell>
                <TableCell sx={{ width: 180 }}>Reason</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {events.isError ? (
                <TableRow>
                  <TableCell colSpan={8} sx={{ py: 2 }}>
                    <QueryError
                      message="Failed to load events."
                      retrying={events.isFetching}
                      onRetry={() => void events.refetch()}
                    />
                  </TableCell>
                </TableRow>
              ) : events.isLoading ? (
                <TableSkeleton rows={8} columns={8} />
              ) : (events.data?.events.length ?? 0) === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} sx={{ py: 6, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      No events match the current filters.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                (events.data?.events ?? []).map((ev) => (
                  <ExplorerEventRow
                    key={ev.id}
                    event={ev}
                    onOpenDevice={() =>
                      navigate(
                        `/devices/${ev.device_id}/software-update?rollout_id=${ev.rollout_id}`,
                      )
                    }
                  />
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {!events.isError && (
          <ExplorerPager
            offset={expOffset}
            limit={EVENT_PAGE_LIMIT}
            total={events.data?.total ?? 0}
            loading={events.isFetching}
            onPrev={() => setExpOffset((o) => Math.max(0, o - EVENT_PAGE_LIMIT))}
            onNext={() => setExpOffset((o) => o + EVENT_PAGE_LIMIT)}
          />
        )}
      </Card>
    </Box>
  );
}

// ─── KPI strip ─────────────────────────────────────────────────────────────────

function QueryError({
  message,
  retrying,
  onRetry,
  inset = false,
}: {
  message: string;
  retrying: boolean;
  onRetry: () => void;
  inset?: boolean;
}) {
  return (
    <Alert
      severity="error"
      variant="outlined"
      sx={{ m: inset ? 2 : 0 }}
      action={
        <Button color="inherit" size="small" disabled={retrying} onClick={onRetry}>
          {retrying ? 'Retrying…' : 'Retry'}
        </Button>
      }
    >
      {message}
    </Alert>
  );
}

function KpiStrip({
  totals,
  loading,
  healthCounts,
}: {
  totals: OverviewTotals | undefined;
  loading: boolean;
  healthCounts: Record<Health, number>;
}) {
  const dash = '—';
  const downloadFailed = totals ? totals.download_failed + totals.download_rejected : 0;
  const downloadOutcomes = totals ? totals.download_success + downloadFailed : 0;
  const downloadFailureRate = downloadOutcomes > 0 ? downloadFailed / downloadOutcomes : 0;
  const installFailed = totals?.install_failed ?? 0;
  const installOutcomes = totals ? totals.install_success + installFailed : 0;
  const installFailureRate = installOutcomes > 0 ? installFailed / installOutcomes : 0;

  const activeRolloutsSubtitle: ReactNode = (() => {
    if (!totals) return 'active';
    const segs: Array<{ key: string; label: string; color: string; count?: number; bold?: boolean }> = [];
    if (healthCounts.failing > 0)
      segs.push({ key: 'f', count: healthCounts.failing, label: 'failing', color: colors.error.dark, bold: true });
    if (healthCounts.degraded > 0)
      segs.push({ key: 'd', count: healthCounts.degraded, label: 'degraded', color: colors.warning.dark, bold: true });
    if (healthCounts.no_activity > 0)
      segs.push({ key: 'n', count: healthCounts.no_activity, label: 'no activity', color: colors.text.disabled });
    if (segs.length === 0)
      segs.push({ key: 'h', label: 'all healthy', color: colors.success.dark, bold: true });
    return segs.map((s, i) => (
      <Box component="span" key={s.key}>
        {i > 0 ? ' · ' : ''}
        {s.count == null ? (
          <Box component="span" sx={{ color: s.color, fontWeight: s.bold ? 600 : 400 }}>
            {s.label}
          </Box>
        ) : (
          <>
            <Box component="span" sx={{ color: s.color, fontWeight: s.bold ? 600 : 400 }}>
              {s.count.toLocaleString()}
            </Box>{' '}
            {s.label}
          </>
        )}
      </Box>
    ));
  })();

  return (
    <Box
      sx={{
        display: 'grid',
        gap: 2,
        gridTemplateColumns: {
          xs: 'repeat(2, 1fr)',
          md: 'repeat(4, 1fr)',
        },
      }}
    >
      <StatTile
        label={
          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
            Active rollouts
            <HealthThresholdHelp />
          </Box>
        }
        value={loading || !totals ? dash : totals.active_rollouts.toLocaleString()}
        subtitle={activeRolloutsSubtitle}
      />
      <StatTile
        label="Devices seen"
        value={loading || !totals ? dash : totals.active_devices.toLocaleString()}
        subtitle="distinct devices in window"
      />
      <StatTile
        label="Download success"
        value={loading || !totals ? dash : totals.download_success.toLocaleString()}
        subtitle={
          totals ? (
            <>
              <Box
                component="span"
                sx={{ color: downloadFailed > 0 ? colors.error.dark : 'text.disabled', fontWeight: downloadFailed > 0 ? 600 : 400 }}
              >
                {downloadFailed.toLocaleString()}
              </Box>{' '}
              failed (
              <Box
                component="span"
                sx={{ color: downloadFailed > 0 ? colors.error.dark : 'text.disabled', fontWeight: downloadFailed > 0 ? 600 : 400 }}
              >
                {(downloadFailureRate * 100).toFixed(1)}%
              </Box>
              )
            </>
          ) : (
            'downloads succeeded'
          )
        }
      />
      <StatTile
        label="Install success"
        value={loading || !totals ? dash : totals.install_success.toLocaleString()}
        subtitle={
          totals ? (
            <>
              <Box
                component="span"
                sx={{ color: installFailed > 0 ? colors.error.dark : 'text.disabled', fontWeight: installFailed > 0 ? 600 : 400 }}
              >
                {installFailed.toLocaleString()}
              </Box>{' '}
              failed (
              <Box
                component="span"
                sx={{ color: installFailed > 0 ? colors.error.dark : 'text.disabled', fontWeight: installFailed > 0 ? 600 : 400 }}
              >
                {(installFailureRate * 100).toFixed(1)}%
              </Box>
              )
            </>
          ) : (
            'installs succeeded'
          )
        }
      />
    </Box>
  );
}

function StatTile({
  label,
  value,
  subtitle,
  valueColor,
}: {
  label: ReactNode;
  value: ReactNode;
  subtitle: ReactNode;
  valueColor?: string;
}) {
  return (
    <Box
      sx={{
        p: 1.75,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: `${radius.lg}px`,
        bgcolor: 'background.paper',
        boxShadow: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: 0.25,
        minWidth: 0,
      }}
    >
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: 10.5 }}
      >
        {label}
      </Typography>
      <Typography sx={{ fontSize: 25, fontWeight: t.fontWeight.semibold, lineHeight: 1.2, color: valueColor }}>
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11.5 }} noWrap>
        {subtitle}
      </Typography>
    </Box>
  );
}

// ─── Now rolling out ─────────────────────────────────────────────────────────

// Header (38px) + ~5 single-line package rows (40px each).
const PACKAGE_INSPECTOR_HEIGHT = 238;

function PackageRolloutInspector({
  packages,
  rollouts,
}: {
  packages: OverviewPackage[];
  rollouts: OverviewRollout[];
}) {
  const [selectedPackageID, setSelectedPackageID] = useState(packages[0]?.id ?? '');
  const selectedPackage = packages.find((pkg) => pkg.id === selectedPackageID) ?? packages[0];
  const packageRollouts = selectedPackage
    ? rollouts.filter(({ rollout }) =>
        rollout.packages.some((attached) => attached.package_id === selectedPackage.id),
      )
    : [];

  if (!selectedPackage) return null;

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'minmax(300px, 38%) minmax(0, 1fr)',
        height: PACKAGE_INSPECTOR_HEIGHT,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: `${radius.md}px`,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          minWidth: 0,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          borderRight: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        <Box
          sx={{
            flexShrink: 0,
            height: 38,
            px: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            bgcolor: colors.background.tableHead,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography sx={{ fontSize: 11, fontWeight: t.fontWeight.semibold, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            Packages
          </Typography>
          <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>
            {packages.length.toLocaleString()}
          </Typography>
        </Box>
        <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
          {packages.map((pkg) => {
            const selected = pkg.id === selectedPackage.id;
            return (
              <Box
                key={`${pkg.id}-${pkg.version}`}
                role="button"
                tabIndex={0}
                aria-pressed={selected}
                onClick={() => setSelectedPackageID(pkg.id)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    setSelectedPackageID(pkg.id);
                  }
                }}
                sx={{
                  position: 'relative',
                  minHeight: 40,
                  px: 1.5,
                  py: 0.75,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'background.paper',
                  cursor: 'pointer',
                  '&:hover': { bgcolor: 'action.hover' },
                  '&:focus-visible': { outline: `2px solid ${colors.primary.main}`, outlineOffset: -2 },
                  ...(selected && {
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      left: 0,
                      top: 0,
                      bottom: 0,
                      width: 3,
                      bgcolor: colors.primary.main,
                    },
                  }),
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'baseline', gap: 1, overflow: 'hidden' }}>
                  <MuiLink
                    component={RouterLink}
                    to={`/software-update/packages/${pkg.id}`}
                    underline="none"
                    onClick={(event) => event.stopPropagation()}
                    sx={{
                      flexShrink: 0,
                      color: 'text.primary',
                      fontSize: 12.5,
                      fontWeight: selected ? t.fontWeight.bold : t.fontWeight.medium,
                      whiteSpace: 'nowrap',
                      '&:hover': { color: colors.primary.main, textDecoration: 'underline' },
                    }}
                  >
                    {overviewPackageLabel(pkg)}
                  </MuiLink>
                  <Typography
                    noWrap
                    sx={{ minWidth: 0, fontSize: 11, color: 'text.secondary' }}
                  >
                    {pkg.type.toUpperCase()} · {pkg.version}
                    {pkg.install_success_count > 0 && (
                      <>
                        {' · '}
                        <Box component="span" sx={{ fontWeight: t.fontWeight.semibold, color: 'text.primary' }}>
                          {pkg.install_success_count.toLocaleString()}
                        </Box>{' '}
                        installed
                      </>
                    )}
                    {pkg.failed_count > 0 && (
                      <>
                        {' · '}
                        <Box component="span" sx={{ fontWeight: t.fontWeight.semibold, color: colors.error.main }}>
                          {pkg.failed_count.toLocaleString()}
                        </Box>{' '}
                        failed
                      </>
                    )}
                  </Typography>
                </Box>
                <Typography sx={{ flexShrink: 0, fontSize: 11, color: 'text.secondary', whiteSpace: 'nowrap' }}>
                  {pkg.rollout_count.toLocaleString()} {pkg.rollout_count === 1 ? 'rollout' : 'rollouts'}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Box>

      <Box sx={{ minWidth: 0, minHeight: 0, bgcolor: '#FAFAFA', overflowY: 'auto' }}>
        <Box
          sx={{
            px: 2,
            py: 1.5,
            display: 'flex',
            alignItems: 'baseline',
            gap: 1,
            bgcolor: 'background.paper',
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <MuiLink
            component={RouterLink}
            to={`/software-update/packages/${selectedPackage.id}`}
            underline="none"
            sx={{
              color: 'text.primary',
              fontSize: 14,
              fontWeight: t.fontWeight.semibold,
              '&:hover': { color: colors.primary.main, textDecoration: 'underline' },
            }}
          >
            {overviewPackageLabel(selectedPackage)}
          </MuiLink>
          <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
            {selectedPackage.version}
          </Typography>
        </Box>

        {packageRollouts.map(({ rollout, stats }) => {
          const health = computeHealth(stats);
          const hasEvents = eventTotal(stats) > 0;
          return (
            <Box
              key={rollout.id}
              sx={{
                minHeight: 44,
                px: 2,
                py: 0.75,
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                borderBottom: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper',
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'baseline', gap: 1.25, overflow: 'hidden' }}>
                <MuiLink
                  component={RouterLink}
                  to={`/software-update/rollouts/${rollout.id}`}
                  underline="none"
                  sx={{
                    flexShrink: 0,
                    color: 'text.primary',
                    fontSize: 12.5,
                    fontWeight: t.fontWeight.medium,
                    whiteSpace: 'nowrap',
                    '&:hover': { color: colors.primary.main, textDecoration: 'underline' },
                  }}
                >
                  {rollout.name || '(unnamed)'}
                </MuiLink>
                <RolloutTargetInline
                  target={rollout.target}
                  displayName={rollout.target_display_name}
                  sx={{ minWidth: 0, flexWrap: 'nowrap' }}
                />
              </Box>
              <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <HealthDot health={health} />
                <Typography
                  sx={{
                    color: 'text.secondary',
                    fontSize: 11,
                    fontVariantNumeric: 'tabular-nums',
                    minWidth: 72,
                    textAlign: 'right',
                  }}
                >
                  {hasEvents ? `${(stats.error_rate * 100).toFixed(1)}% errors` : 'No events yet'}
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

// ─── Active rollouts row ─────────────────────────────────────────────────────

function RolloutRow({ entry }: { entry: OverviewRollout }) {
  const { rollout, stats } = entry;
  const health = computeHealth(stats);
  const cfg = HEALTH_CONFIG[health];
  const fail = stats.download_failed + stats.download_rejected + stats.install_failed;
  const unhealthy = health === 'failing' || health === 'degraded';

  return (
    <TableRow hover sx={{ '& td': { py: 1.25 } }}>
      <TableCell>
        <HealthDot health={health} />
      </TableCell>
      <TableCell>
        <MuiLink
          component={RouterLink}
          to={`/software-update/rollouts/${rollout.id}`}
          underline="none"
          sx={{
            fontSize: 13,
            fontWeight: t.fontWeight.medium,
            color: 'text.primary',
            '&:hover': { color: colors.primary.main, textDecoration: 'underline' },
          }}
        >
          {rollout.name || '(unnamed)'}
        </MuiLink>
      </TableCell>
      <TableCell>
        <Box
          component="span"
          sx={{
            fontSize: 11,
            fontWeight: t.fontWeight.medium,
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
            color: 'text.secondary',
          }}
        >
          {ROLLOUT_TYPE_CHIP[rollout.type].label}
        </Box>
      </TableCell>
      <TableCell>
        <RolloutTargetInline target={rollout.target} displayName={rollout.target_display_name} />
      </TableCell>
      <TableCell align="right">
        <NumCell value={stats.download_success} />
      </TableCell>
      <TableCell align="right">
        <NumCell value={stats.install_success} />
      </TableCell>
      <TableCell align="right">
        <NumCell value={fail} muted danger={unhealthy} />
      </TableCell>
      <TableCell>
        <ErrorRateMeter
          errorRate={stats.error_rate}
          hasEvents={eventTotal(stats) > 0}
          color={cfg.color}
          trackBg={cfg.bg}
        />
      </TableCell>
      <TableCell>
        <Sparkline data={stats.sparkline ?? []} />
      </TableCell>
      <TableCell align="right">
        {stats.last_event_at ? (
          <FormattedTime
            value={stats.last_event_at}
            variant="relative"
            color="text.secondary"
            sx={{ fontSize: 12 }}
          />
        ) : (
          <Dash />
        )}
      </TableCell>
    </TableRow>
  );
}

// Plain status dot + label — no pill background/border.
function HealthDot({ health }: { health: Health }) {
  const cfg = HEALTH_CONFIG[health];
  return (
    <Box
      component="span"
      sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, whiteSpace: 'nowrap' }}
    >
      <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: cfg.color, flexShrink: 0 }} />
      <Box component="span" sx={{ fontSize: 12, color: 'text.secondary' }}>
        {cfg.label}
      </Box>
    </Box>
  );
}

function ErrorRateMeter({
  errorRate,
  hasEvents,
  color,
  trackBg,
}: {
  errorRate: number;
  hasEvents: boolean;
  color: string;
  trackBg: string;
}) {
  const pct = Math.min(100, Math.max(0, errorRate * 100));
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Typography
        sx={{
          fontVariantNumeric: 'tabular-nums',
          fontSize: 12.5,
          minWidth: 42,
          color: hasEvents ? 'text.primary' : 'text.disabled',
        }}
      >
        {hasEvents ? `${pct.toFixed(1)}%` : '—'}
      </Typography>
      <Box
        sx={{
          width: 56,
          height: 5,
          borderRadius: '3px',
          bgcolor: trackBg,
          position: 'relative',
          overflow: 'hidden',
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: `${pct}%`,
            bgcolor: color,
            borderRadius: '3px',
          }}
        />
      </Box>
    </Box>
  );
}

function Sparkline({ data }: { data: number[] }) {
  const w = 88;
  const h = 24;
  if (data.length === 0) return <Dash />;
  const max = Math.max(...data, 1);
  const n = data.length;
  const points = data
    .map((v, i) => {
      const x = n <= 1 ? w / 2 : 3 + (i * (w - 6)) / (n - 1);
      const y = h - 3 - ((h - 7) * v) / max;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');
  return (
    <svg width={w} height={h} style={{ display: 'block' }} aria-hidden>
      <polyline
        points={points}
        fill="none"
        stroke={colors.grey[400]}
        strokeWidth={1.5}
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </svg>
  );
}

function NumCell({
  value,
  danger,
  muted,
}: {
  value: number;
  /** Error color + weight for a nonzero value (e.g. failing/degraded rows). */
  danger?: boolean;
  /** Neutral secondary color for a nonzero value when not `danger`. */
  muted?: boolean;
}) {
  const isZero = value === 0;
  const color = isZero
    ? 'text.disabled'
    : danger
      ? colors.error.dark
      : muted
        ? 'text.secondary'
        : 'text.primary';
  return (
    <Typography
      component="span"
      sx={{
        fontFamily: 'monospace',
        fontVariantNumeric: 'tabular-nums',
        fontSize: 12.5,
        color,
        fontWeight: danger && !isZero ? t.fontWeight.semibold : t.fontWeight.normal,
      }}
    >
      {isZero ? '—' : value.toLocaleString()}
    </Typography>
  );
}

// ─── Failure reasons ─────────────────────────────────────────────────────────

function ReasonBar({
  reason,
  count,
  max,
}: {
  reason: string;
  count: number;
  max: number;
}) {
  const pct = Math.min(100, (100 * count) / max);
  return (
    <Box sx={{ py: 0.75 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.5, gap: 1 }}>
        <Typography sx={{ fontFamily: 'monospace', fontSize: 12 }} noWrap>
          {reason}
        </Typography>
        <Typography sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums', fontSize: 12.5 }}>
          {count.toLocaleString()}
        </Typography>
      </Box>
      <Box
        sx={{
          height: 4,
          borderRadius: '2px',
          bgcolor: colors.error.light,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: `${pct}%`,
            bgcolor: colors.error.main,
            borderRadius: '2px',
          }}
        />
      </Box>
    </Box>
  );
}

// ─── Problem devices ─────────────────────────────────────────────────────────

function ProblemDeviceRow({ device }: { device: OverviewProblemDevice }) {
  const label = device.machine_id || device.serial_number || device.device_id;
  return (
    <TableRow hover sx={{ '& td': { py: 1.25 } }}>
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
          <TextLink
            to={`/devices/${device.device_id}/software-update`}
            sx={{ ...EXPLORER_LINK_SX, ...ELLIPSIS_SX }}
          >
            {label}
          </TextLink>
          <InternalDeviceIcon internal={device.is_internal} size={14} />
          <CopyDeviceId machineId={device.machine_id} fallback={label} size={14} sx={{ p: 0.25 }} />
        </Box>
      </TableCell>
      <TableCell align="center">
        <Typography
          sx={{ fontVariantNumeric: 'tabular-nums', fontSize: 12.5, fontWeight: 600, color: colors.error.dark }}
        >
          {device.failure_count.toLocaleString()}
        </Typography>
      </TableCell>
      <TableCell>{device.last_reason ? <ReasonChip label={device.last_reason} /> : <Dash />}</TableCell>
      <TableCell align="right">
        <FormattedTime
          value={device.last_event_at}
          variant="relative"
          color="text.secondary"
          sx={{ fontSize: 12 }}
        />
      </TableCell>
    </TableRow>
  );
}

// ─── Stuck OS installs ───────────────────────────────────────────────────────

const STUCK_COLUMNS = 6;
// Fixed-layout columns truncate long device IDs and rollout names instead of
// stretching the table.
const ELLIPSIS_SX = { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } as const;

function StuckInstallsCard({
  items,
  total,
  asOf,
  loading,
  fetching,
  error,
  hasData,
  offset,
  onRetry,
  onPrev,
  onNext,
}: {
  items: OverviewStuckInstall[];
  total: number;
  /** Server snapshot time; "ago" labels are measured from it. */
  asOf: string;
  loading: boolean;
  fetching: boolean;
  error: boolean;
  hasData: boolean;
  offset: number;
  onRetry: () => void;
  onPrev: () => void;
  onNext: () => void;
}) {
  const header = (caption: ReactNode) => (
    <CardSectionHeader
      title="Stuck OS installs"
      icon={<StuckIcon sx={{ color: SECTION_ICON_COLOR, fontSize: 20 }} />}
      extra={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <HelpTip title={STUCK_INSTALL_HELP} />
          {caption && (
            <Typography variant="caption" color="text.disabled">
              {caption}
            </Typography>
          )}
        </Box>
      }
    />
  );

  if (error && !hasData) {
    return (
      <Card sx={{ overflow: 'hidden' }}>
        {header(null)}
        <QueryError message="Failed to load stuck OS installs." retrying={fetching} onRetry={onRetry} inset />
      </Card>
    );
  }

  // Stuck installs are rare; with nothing to show the card collapses to its header.
  if (!loading && total === 0 && offset === 0) {
    return (
      <Card sx={{ overflow: 'hidden' }}>
        {header('No OS install waiting on a result for 24h or more.')}
        <Box sx={{ pb: 2 }} />
      </Card>
    );
  }

  return (
    <Card sx={{ overflow: 'hidden' }}>
      {header(null)}
      {error && (
        <QueryError
          message="Failed to refresh stuck OS installs. Showing the previous result."
          retrying={fetching}
          onRetry={onRetry}
          inset
        />
      )}
      <TableContainer>
        <Table size="small" sx={{ ...TABLE_SX, tableLayout: 'fixed' }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: '19%' }}>Device</TableCell>
              <TableCell sx={{ width: '21%' }}>Rollout</TableCell>
              <TableCell sx={{ width: '13%' }}>To version</TableCell>
              <TableCell sx={{ width: '20%' }}>Latest event</TableCell>
              <TableCell sx={{ width: '16%' }}>Downloaded</TableCell>
              <TableCell align="right" sx={{ width: '11%' }}>Stuck for</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableSkeleton rows={3} columns={STUCK_COLUMNS} />
            ) : items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={STUCK_COLUMNS} sx={{ py: 5, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    No stuck OS installs on this page.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              items.map((item) => <StuckInstallRow key={item.command_item_id} item={item} asOf={asOf} />)
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <ExplorerPager
        offset={offset}
        limit={STUCK_INSTALL_PAGE_LIMIT}
        total={total}
        loading={fetching}
        onPrev={onPrev}
        onNext={onNext}
      />
    </Card>
  );
}

function StuckInstallRow({ item, asOf }: { item: OverviewStuckInstall; asOf: string }) {
  const deviceLabel = item.machine_id || item.serial_number || item.device_id;
  return (
    <TableRow hover sx={EVENT_ROW_SX}>
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
          <TextLink
            to={`/devices/${item.device_id}/software-update?rollout_id=${item.rollout_id}`}
            sx={{ ...EXPLORER_LINK_SX, ...ELLIPSIS_SX }}
          >
            {deviceLabel}
          </TextLink>
          <InternalDeviceIcon internal={item.is_internal} size={14} />
          <CopyDeviceId machineId={item.machine_id} fallback={deviceLabel} size={14} sx={{ p: 0.25 }} />
        </Box>
      </TableCell>
      <TableCell>
        <TextLink
          to={`/software-update/rollouts/${item.rollout_id}`}
          sx={{ ...EXPLORER_LINK_SX, ...ELLIPSIS_SX, display: 'block' }}
        >
          {item.rollout_name || '(unnamed)'}
        </TextLink>
      </TableCell>
      <EventToVersionCell targetVersion={item.target_version} />
      <TableCell>
        {item.latest_event_type ? (
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', columnGap: 1, rowGap: 0.5 }}>
            <EventChip
              label={item.latest_event_type}
              {...getDeviceUpdateEventVisual(item.latest_event_type)}
            />
            {item.latest_event_at && <TimeAgo value={item.latest_event_at} now={asOf} />}
          </Box>
        ) : (
          <Dash />
        )}
      </TableCell>
      <EventTimeCell value={item.downloaded_at} />
      <TableCell align="right">
        <Typography
          sx={{ fontSize: 13, fontWeight: t.fontWeight.semibold, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}
        >
          {formatStuckDuration(item.stuck_seconds)}
        </Typography>
      </TableCell>
    </TableRow>
  );
}

/**
 * Always-relative time ("3d ago", "12d ago") measured from `now`. FormattedTime
 * switches to an absolute date past 7 days, which read inconsistently in a
 * column where most values are several days old. The exact time stays in the
 * tooltip.
 */
function TimeAgo({ value, now }: { value: string; now: string }) {
  const { formatTime } = useTimezone();
  const minutes = Math.max(0, Math.floor((new Date(now).getTime() - new Date(value).getTime()) / 60_000));
  const label =
    minutes < 1
      ? 'Just now'
      : minutes < 60
        ? `${minutes}m ago`
        : minutes < 24 * 60
          ? `${Math.floor(minutes / 60)}h ago`
          : `${Math.floor(minutes / (24 * 60))}d ago`;
  return (
    <Tooltip title={formatTime(value)} arrow placement="top">
      <Typography component="span" color="text.secondary" sx={{ fontSize: 12, whiteSpace: 'nowrap' }}>
        {label}
      </Typography>
    </Tooltip>
  );
}

/** "5d 3h" style elapsed time; stuck items are always at least a day old. */
function formatStuckDuration(seconds: number): string {
  const totalHours = Math.floor(seconds / 3600);
  const days = Math.floor(totalHours / 24);
  const hours = totalHours % 24;
  return days > 0 ? `${days}d ${hours}h` : `${hours}h`;
}

// ─── Event explorer ──────────────────────────────────────────────────────────

function ExplorerEventRow({
  event,
  onOpenDevice,
}: {
  event: OverviewEvent;
  onOpenDevice: () => void;
}) {
  const visual = getDeviceUpdateEventVisual(event.event_type);
  const deviceLabel = event.machine_id || event.serial_number || event.device_id;
  return (
    <TableRow hover sx={EVENT_ROW_SX}>
      <EventTimeCell value={event.server_created_at} />
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <TextLink onClick={onOpenDevice} sx={EXPLORER_LINK_SX}>
            {deviceLabel}
          </TextLink>
          <InternalDeviceIcon internal={event.is_internal} size={14} />
          <CopyDeviceId machineId={event.machine_id} fallback={deviceLabel} size={14} sx={{ p: 0.25 }} />
        </Box>
      </TableCell>
      <TableCell>
        <TextLink to={`/software-update/rollouts/${event.rollout_id}`} sx={EXPLORER_LINK_SX}>
          {event.rollout_name || '(unnamed)'}
        </TextLink>
      </TableCell>
      <TableCell>
        {event.package_display_name || event.package_name ? (
          <Typography variant="body2" fontSize={13}>
            {event.package_display_name || event.package_name}
          </Typography>
        ) : (
          <Dash />
        )}
      </TableCell>
      <EventFromVersionCell fromVersion={event.from_version} />
      <EventToVersionCell targetVersion={event.package_version} />
      <TableCell>
        <EventChip label={event.event_type} {...visual} />
      </TableCell>
      <TableCell>{event.reason ? <ReasonChip label={event.reason} /> : <Dash />}</TableCell>
    </TableRow>
  );
}

function ExplorerPager({
  offset,
  limit,
  total,
  loading,
  onPrev,
  onNext,
}: {
  offset: number;
  limit: number;
  total: number;
  loading: boolean;
  onPrev: () => void;
  onNext: () => void;
}) {
  const from = total === 0 ? 0 : offset + 1;
  const to = Math.min(offset + limit, total);
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: 1.5,
        px: 2,
        py: 1,
        borderTop: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Typography variant="caption" color="text.secondary">
        {loading ? 'Loading…' : `${from.toLocaleString()}–${to.toLocaleString()} of ${total.toLocaleString()}`}
      </Typography>
      <Button
        size="small"
        variant="outlined"
        disabled={offset === 0 || loading}
        onClick={onPrev}
        sx={{ borderColor: 'divider', color: 'text.secondary', textTransform: 'none', fontSize: 12, py: 0.25 }}
      >
        Prev
      </Button>
      <Button
        size="small"
        variant="outlined"
        disabled={to >= total || loading}
        onClick={onNext}
        sx={{ borderColor: 'divider', color: 'text.secondary', textTransform: 'none', fontSize: 12, py: 0.25 }}
      >
        Next
      </Button>
    </Box>
  );
}

function EmptyRow({ children }: { children: ReactNode }) {
  return (
    <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
      {children}
    </Typography>
  );
}

export default SoftwareUpdateOverviewPage;
